import React, { useEffect, useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/cycle-api';

import { useFormState } from '../../../../hooks/useFormState';
import { useOverlayState } from '../../../../hooks/useOverlayState';
import { useSwrRequestKeysToMutateFromContext } from '../../../../hooks/useSwrRequestKeysToMutateFromContext';

import { defaultErrorMessage, invalidJsonErrorMessage } from '../../../../constants/errorMessages';

import { mutateListDataAfterUpdatingItemWithCb } from '../../../../helpers/swrMutateUtils';
import { getFormattedJsonInputValue, isValidJsonInput } from '../../../../helpers/isValidJson';

import Panel from '../../../../components/Panel';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import PanelLayout from '../../../../components/PanelLayout/PanelLayout';
import CycleStageForm from '../CycleStageForm/CycleStageForm';
import DeleteStageModal from '../DeleteStageModal/DeleteStageModal';
import MultiActionButton from '../../../../design-system/MultiActionButton/MultiActionButton';
import CycleStageMultiActionPopup from '../CycleStageMultiActionPopup/CycleStageMultiActionPopup';
import { Button } from '../../../../design-system';

const EditCycleStagePanel = ({
    stage,
    setStage,
    isDefault,
    isEditable,
    isDeleteAvailable,
    stageFullData,
    setStageFullData,
    cycleId,
    onClose,
}) => {
    const { id, name } = stage;

    const { formData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } = useFormState(
        () => ({ details: stageFullData?.default_version?.details })
    );

    const { isOpened: isPopupOpened, open: openPopup, close: closePopup } = useOverlayState();
    const { isOpened: isModalOpened, open: openModal, close: closeModal } = useOverlayState();

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const {
        requestKeysToMutate: { cycles: requestKeysToMutate },
    } = useSwrRequestKeysToMutateFromContext();

    useEffect(() => {
        if (!!stageFullData) return;

        const fetchStageData = async () => {
            try {
                const { data } = await client.get(API.ROUTES.cycle.cycleStage + id + '/');
                setStageFullData(data);

                const details = data.default_version.details;
                handleInputChange('details', details);
            } catch (e) {
                onClose();
            }
        };

        void fetchStageData();
    }, []);

    const areDetailsChanged = typeof formData.details === 'string';
    const areDetailsFilled = !!formData.details;

    const getRequestBody = (newStatus) => {
        const payload = {};
        if (newStatus) {
            payload.stage_status = newStatus;
        }

        if (areDetailsChanged) {
            const details = getFormattedJsonInputValue(formData.details);
            payload.details = details;
        }
        return payload;
    };

    const handleSaveChanges = async ({ status } = {}) => {
        try {
            const isValid = isValidJsonInput(formData.details);
            if (!isValid) {
                setFieldErrorMessages({ details: invalidJsonErrorMessage });

                return;
            }

            if (!status) {
                setIsLoading(true);
            }

            const payload = getRequestBody(status);
            const { data } = await client.patch(API.ROUTES.cycle.cycleStage + id + '/', payload);

            const { stage_status, default_version, type, name } = data;

            setStage((stage) => ({ ...stage, stage_status }));
            setStageFullData((fullData) => ({ ...fullData, default_version }));

            if (isDefault) {
                mutateListDataAfterUpdatingItemWithCb({
                    requestKeysToMutate,
                    id: cycleId,
                    cb: (item) => {
                        if (item.current_stage_id === id) {
                            return {
                                ...item,
                                current_stage: {
                                    ...item.current_stage,
                                    stage_status,
                                    name,
                                    type,
                                },
                            };
                        }
                        return item;
                    },
                });
            }
            onClose();
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    const submitButtonState = isLoading
        ? 'loading'
        : isEditable && areDetailsChanged && areDetailsFilled
        ? 'default'
        : 'disabled';

    const renderButtonsGroup = () => {
        return (
            <div className="relative flex items-center justify-end gap-6">
                {isEditable && (
                    <Button
                        size="sm"
                        type="link"
                        text="Delete Stage"
                        state={isDeleteAvailable ? 'default' : 'disabled'}
                        underlined
                        onClick={openModal}
                    />
                )}

                <MultiActionButton
                    size="sm"
                    type="secondary"
                    text="Save Changes"
                    state={submitButtonState}
                    buttonIconState={isEditable ? 'default' : 'disabled'}
                    onClick={handleSaveChanges}
                    isPopupOpened={isPopupOpened}
                    togglePopup={openPopup}
                />

                {isPopupOpened && (
                    <CycleStageMultiActionPopup
                        handleSaveChanges={handleSaveChanges}
                        onClose={closePopup}
                    />
                )}
            </div>
        );
    };

    const areDetailsLoading = !stageFullData;

    return (
        <Panel onClose={onClose} showControls={false} withOverflowProperty={false} resetPadding>
            <PanelLayout
                title="Edit Cycle Stage"
                renderButtonsGroup={renderButtonsGroup}
                isLoading={areDetailsLoading}
                onClose={onClose}
            >
                <CycleStageForm
                    stage={stage}
                    formData={formData}
                    fieldErrorMessages={fieldErrorMessages}
                    handleInputChange={handleInputChange}
                    isEditable={isEditable}
                />
            </PanelLayout>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />

            {isModalOpened && (
                <DeleteStageModal
                    id={id}
                    name={name}
                    setStage={setStage}
                    onClose={closeModal}
                    onClosePanel={onClose}
                />
            )}
        </Panel>
    );
};

export default EditCycleStagePanel;
